'use client';
import { useContext, useEffect, useState } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { syncGlassboxAccount } from '@/helpers/utils/glassbox';
import {
  PathNames,
  extractCustomerId,
  getPageUrl,
  TranslateText,
  getSelectedDivision,
  SetCookie,
} from '@/helpers/utils/siteUtils';
import { OrderDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import AdjustedItems from './components/AdjustedItems';
import OrderCreditSummary from './components/CreditSummary/creditSummary';
import OrderDetails from './components/Details/OrderDetails';
import OrderNotes from './components/Notes/orderNotes';
import OrderItems from './components/OrderItems/orderItems';
import Orderpayment from './components/OrderPayment/orderPayment';
import OrderShipping from './components/OrderShipping/orderShipment';
import OrderSummary from './components/OrderSummary/orderSummary';
import OrderTotalDetails from './components/OrderTotalDetails';
import PostOrderActivity from './components/PostOrderActivity/postOrderActivity';
import ReturnTrackingDetails from './components/PostOrderActivity/returnTracking';
import { PageTexts } from './i18n';
import { OrderDataType } from './types';
import { transformOrderDetails } from './util';
import { COOKIE_KEYS, DEFAULT_SITE_LANG, ORDER_TYPES } from '../../../../constants/constant';
import { convertDateFormat } from '../../utils/formatUtils';
import SoldAccountBanner from '../customer-detail/components/soldAccountBanner';
import {
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
  DATE_FORMAT_MONTH_DATE_YYYY,
  ISO_DATE_TIME_FORMAT,
} from '../customer-detail/constants';
import BackButton from '../ui/back-button';
import Loader from '../ui/loader';

const OrderDetailsPage = ({ orderData, data }: { orderData: OrderDataType; data: any }) => {
  const {
    orderDetails,
    orderNotes,
    shippingAndBillingDetails,
    RecipientAddresses,
    lineItems,
    customerId,
    isOrderReleasable,
    InvoiceData,
    returnTrackingDetails,
    payRecords,
    lineItemsWithAdjustments,
    orderTotalDetails,
    RecipientLineItems,
    canSendTracer,
    showOrderAdjustment,
    isOrderCancelable,
    GroupedItems,
    showAdvanceShippingOption,
    isSignatureRequired,
    isAddressAdjustable,
    isOrderAddressEditable,
    EstimatedTotal,
    isShippingMethodAdjustable,
    billingAddress,
    paymentMethods,
    orderSummaryInfo,
    canSendReturnLabel,
    orderBalance,
    adjustedItems,
    giftMessage,
    isOrderHoldable,
  } = transformOrderDetails(orderData);
  const { setCustomerData } = useContext(CreditSummaryContext);
  const { resetCart, data: cartData } = useCart(false);
  const pathName = usePathname();
  const router = useRouter();
  const params = useSearchParams();
  const { getOrderDetails, getCustomerDetails } = useAccount();
  const [loading, setLoading] = useState(false);
  const [customerFetched, setCustomerFetched] = useState(false);
  const [notes, setNotes] = useState(orderNotes);
  const [totalNotes, setTotalNotes] = useState(orderNotes?.length);
  const [backUrl, setBackUrl] = useState('');
  const [showBackToOrderSearch, setShowBackToOrderSearch] = useState(false);
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [debtBuyerCode, setDebtBuyerCode] = useState('');
  const [accountSoldOn, setAccountSoldOn] = useState('');
  const [creditSoldAccountId, setCreditSoldAccountId] = useState('');
  const [customerDetailsUrl, setCustomerDetailsUrl] = useState('');
  const [isCustomerDetailsUrlStored, setIsCustomerDetailsUrlStored] = useState(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const showActiveBrandSection = useShowActiveBrandSection();
  const isAssetSaleEnabled = projectSettings?.assetSaleEnabled;
  const timestamp = params.get('timestamp') ?? null;
  const isMailOrder = orderDetails?.origin === 'Mail' && orderDetails?.originalNumOfLines === '0';
  const batchId = orderData?.Payments?.[1]?.PaymentDetail?.batchId;
  const fromOrder = params.get('fromOrder') ?? null;
  const fromCustomerSearch = params.get('fromCustomerSearch') === 'true';

  const resetCartData = async () => {
    const divisionCode = getSelectedDivision() ?? 'ZB';
    await resetCart({
      omsCustomerId: customerId,
      orderType: ORDER_TYPES.NEW_ORDER,
      divisionCode: divisionCode,
    });
  };

  useEffect(() => {
    const urlHistory: any = getCookie(COOKIE_KEYS.URL_HISTORY);

    if (!urlHistory || !customerDetailsUrl) return;

    const parsedUrl = JSON.parse(urlHistory);
    const newHistory = { ...parsedUrl };
    const existingEntry = newHistory[PathNames.CUSTOMER_DETAILS];
    const linkCustomerId = extractCustomerId(existingEntry?.link) ?? '';
    const previousCartId = cartData?.omsCustomerId;
    const sameCustomerInHistory = linkCustomerId === customerId;
    const sameCustomerInCart = previousCartId === customerId;

    if (sameCustomerInHistory && sameCustomerInCart) return;

    newHistory[PathNames.CUSTOMER_DETAILS] = {
      link: customerDetailsUrl,
      name: '',
    };
    if (!sameCustomerInCart) {
      resetCartData();
    }
    setIsCustomerDetailsUrlStored(true);
    SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(newHistory));
  }, [customerId, customerDetailsUrl, cartData?.omsCustomerId]);

  const getUpdatedData = () => {
    setLoading(true);
    const urlSearchParams = new URLSearchParams(params.toString());
    const timestamp = new Date().toISOString();
    if (urlSearchParams.has('timestamp')) {
      urlSearchParams.set('timestamp', timestamp);
    } else {
      urlSearchParams.append('timestamp', timestamp);
    }
    const newUrl = `${pathName}?${urlSearchParams.toString()}`;
    router.push(newUrl);
  };

  const addNewNotes = async () => {
    const payload = {
      divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      orderId: orderDetails?.orderId,
    } as OrderDetailsPayload;

    await getOrderDetails(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const newAddedNotes = transformOrderDetails(response?.Order as OrderDataType);
        setNotes(newAddedNotes?.orderNotes);
        setTotalNotes(newAddedNotes?.orderNotes?.length);
      }
    });
  };

  useEffect(() => {
    setCustomerData({ customerId: customerId });
    const urlHistory = getCookie(COOKIE_KEYS.URL_HISTORY);
    if (urlHistory) {
      const parsedUrl = JSON.parse(urlHistory);
      const CustomerLink = parsedUrl[PathNames.CUSTOMER_DETAILS];
      if (CustomerLink && !isCustomerDetailsUrlStored) {
        const linkCustomerId = extractCustomerId(CustomerLink?.link) ?? '';
        if (fromCustomerSearch) {
          setShowBackToOrderSearch(false);
          setBackUrl(getPageUrl(PathNames.CUSTOMER_DETAILS));
        } else if (fromOrder) {
          setShowBackToOrderSearch(true);
          setBackUrl(getPageUrl(PathNames.ORDER_SEARCH));
        } else {
          setShowBackToOrderSearch(customerId !== linkCustomerId);
          setBackUrl(getPageUrl(customerId === linkCustomerId ? PathNames.CUSTOMER_DETAILS : PathNames.ORDER_SEARCH));
        }
      } else {
        setShowBackToOrderSearch(true);
        setBackUrl(getPageUrl(PathNames.ORDER_SEARCH));
      }
    }
    if (customerId) {
      syncGlassboxAccount(customerId);
      const currentUrlData = {
        customerId: customerId,
        omsCustomerId: customerId,
        divisionCode: orderData?.OrderInfo?.divisionCode,
        idName: 'customerId',
      };
      setCookie(COOKIE_KEYS.CDP_URL, JSON.stringify(currentUrlData), {
        sameSite: 'none',
        secure: true,
        partitioned: true,
      });
    }
  }, [customerId]);

  useEffect(() => {
    setLoading(false);
  }, [timestamp]);

  const getCustomerDetailsLocally = async () => {
    setLoading(true);
    const data = JSON.parse(getCookie(COOKIE_KEYS.CDP_URL) ?? '');
    try {
      const customerId = orderDetails ? orderData?.OrderInfo?.omsCustomerId : data?.customerId;
      const divisionCode = orderDetails ? orderData?.OrderInfo?.divisionCode : data?.divisionCode;
      const customerDetailsResponse = await getCustomerDetails({
        query: {
          customerId: customerId,
          divisionCode: divisionCode,
          idName: 'customerId',
        },
      });
      if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
        const customerDetailsUrl = `/customer-details/?customerId=${customerDetailsResponse?.Customer?.customerId}&omsCustomerId=${customerDetailsResponse?.Customer?.CustomerInfo?.omsCustomerId}&durationInMonths=24&divisionCode=${divisionCode}&idName=customerId`;
        setCustomerDetailsUrl(customerDetailsUrl);
        setCustomerDetails(customerDetailsResponse);
        setCustomerFetched(true);
        const isSoldAccount = isAssetSaleEnabled
          ? customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ??
            false
          : false;
        setIsSoldAccount(isSoldAccount);
        const debtBuyerCode =
          customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.debtBuyerCode ?? '';
        setDebtBuyerCode(debtBuyerCode);
        const accountSoldOn =
          customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.accountSoldOn ?? '';
        setAccountSoldOn(convertDateFormat(accountSoldOn, ISO_DATE_TIME_FORMAT, DATE_FORMAT_MONTH_DATE_YYYY));
        const creditSoldAccountId =
          customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.creditAccountId ?? '';
        setCreditSoldAccountId(creditSoldAccountId);
      }
    } catch (err) {
      console.error(err);
      setCustomerFetched(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCustomerDetailsLocally();
  }, []);

  return (
    <section className="px-6 py-5">
      {loading && <Loader />}
      <section className="flex items-center">
        <p className="mr-auto pb-5 text-xl font-bold">{Texts.orderDetailsHeading}</p>
        <BackButton
          text={showBackToOrderSearch ? Texts.backToOrderSearch : Texts.backToCustomerDetails}
          className="flex items-center text-blueLight underline"
          url={backUrl}
        />
      </section>
      <section className="flex flex-wrap gap-4 lg:flex-nowrap">
        <section className="w-full lg:w-4/5">
          <section className={`${isAssetSaleEnabled && isSoldAccount ? 'mb-5' : ''}`}>
            <SoldAccountBanner
              isSoldAccount={isSoldAccount}
              creditAccountNumber={creditSoldAccountId}
              debtBuyerCode={debtBuyerCode}
              accountSoldOn={accountSoldOn}
            />
          </section>
          <div className="mb-5 flex flex-col gap-4">
            <OrderDetails
              orderDetails={orderDetails}
              getUpdatedData={getUpdatedData}
              isOrderShipped={orderData?.PostSubmissionInfo?.AvailableActions?.isAppeasable}
              customerId={customerId}
              addNewNotes={addNewNotes}
              showOrderAdjustment={showOrderAdjustment}
              isOrderCancelable={isOrderCancelable}
              EstimatedTotal={EstimatedTotal}
              order={orderData}
              customerDetails={customerDetails}
              loading={loading}
              isSoldAccount={isSoldAccount}
              showActiveBrandSection={showActiveBrandSection}
            />
            <OrderNotes
              totalNotes={totalNotes}
              notes={notes}
              addNewNotes={addNewNotes}
              showActiveBrandSection={showActiveBrandSection}
            />
            <OrderItems
              data={data}
              RecipientAddresses={RecipientAddresses}
              lineItems={lineItems}
              customerId={customerId}
              addNewNotes={addNewNotes}
              RecipientLineItems={RecipientLineItems}
              canSendTracer={canSendTracer}
              getUpdatedData={getUpdatedData}
              GroupedItems={GroupedItems}
              showAdvanceShippingOption={showAdvanceShippingOption}
              isSignatureRequired={isSignatureRequired}
              canSendReturnLabel={canSendReturnLabel}
              emailAddress={orderDetails?.emailAddress}
              showActiveBrandSection={showActiveBrandSection}
              insuranceInfo={orderSummaryInfo?.insuranceInfo}
              firstName={orderData?.BillingAddress?.PersonInfo?.firstName}
            />
            <AdjustedItems Texts={Texts} adjustedItems={adjustedItems} getUpdatedData={getUpdatedData} />
            <OrderTotalDetails Texts={Texts} orderTotalDetails={orderTotalDetails} />
          </div>
          <section className="mb-5 justify-between gap-7 lg:flex">
            {isMailOrder ? (
              <></>
            ) : (
              <OrderShipping
                shippingDetails={shippingAndBillingDetails}
                RecipientAddresses={RecipientAddresses}
                customerId={customerId}
                isAddressAdjustable={isAddressAdjustable}
                isOrderAddressEditable={isOrderAddressEditable}
                giftMessage={giftMessage}
                showActiveBrandSection={showActiveBrandSection}
              />
            )}

            <Orderpayment
              billingDetails={shippingAndBillingDetails}
              customerId={customerId}
              isShippingMethodAdjustable={isShippingMethodAdjustable}
              billingAddress={billingAddress}
              paymentMethods={paymentMethods}
              getUpdatedData={getUpdatedData}
              orderBalance={orderBalance}
              addNewNotes={addNewNotes}
              batchId={batchId}
              showActiveBrandSection={showActiveBrandSection}
            />
          </section>
          <div className="flex flex-col gap-4">
            <ReturnTrackingDetails Texts={Texts} returnTrackingDetails={returnTrackingDetails} />
            <PostOrderActivity
              Invoices={InvoiceData}
              payRecords={payRecords}
              lineItemsWithAdjustments={lineItemsWithAdjustments}
            />
          </div>
        </section>

        <section className="w-full lg:w-1/5">
          <div className="flex flex-col gap-4">
            <OrderSummary
              Texts={Texts}
              isOrderReleasable={isOrderReleasable}
              addNewNotes={addNewNotes}
              customerId={customerId}
              isOrderCancelable={isOrderCancelable}
              getUpdatedData={getUpdatedData}
              orderSummaryInfo={orderSummaryInfo}
              isOrderHoldable={isOrderHoldable}
              showActiveBrandSection={showActiveBrandSection}
            />
            {customerFetched && (
              <OrderCreditSummary
                customerID={customerId}
                isSoldAccount={isSoldAccount}
                soaCustomer={customerDetails?.Customer}
              />
            )}
          </div>
        </section>
      </section>
    </section>
  );
};

export default OrderDetailsPage;
